import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import useTimeSlots from '../../hooks/useTimeSlots';

/**
 * Time Slot Selector Component
 * Shows available time slots for a specific date when scheduling interviews
 * Optimized for real-time conflict detection and efficient rendering
 */
const TimeSlotSelector = ({ 
  selectedDate, 
  employerId, 
  onSlotSelect, 
  selectedSlot = null,
  duration = 60,
  excludeInterviewId = null, // For rescheduling - exclude current interview
  onOpenTimeSlotManager = null, // Callback to open time slot manager modal
  className = ''
}) => {
  const navigate = useNavigate();
  const { timeSlots, operations, loading: timeSlotsLoading } = useTimeSlots(employerId);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadAvailableSlots = useCallback(async (date, dur, excludeId, ops) => {
    try {
      setLoading(true);
      setError(null);

      const slots = await ops.getAvailableSlots(date, dur, excludeId);
      setAvailableSlots(slots);
    } catch (err) {
      console.error('Error loading available slots:', err);
      setError('Failed to load available time slots. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies to prevent recreating

  // Load available slots when date, employer, or duration changes
  useEffect(() => {
    if (selectedDate && employerId && operations?.getAvailableSlots && !timeSlotsLoading) {
      loadAvailableSlots(selectedDate, duration, excludeInterviewId, operations);
    }
  }, [selectedDate, employerId, duration, excludeInterviewId, timeSlotsLoading, loadAvailableSlots]);

  const formatTime = useCallback((date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }, []);

  const formatTimeRange = useCallback((start, end) => {
    return `${formatTime(start)} - ${formatTime(end)}`;
  }, [formatTime]);

  const handleSlotClick = useCallback((slot) => {
    if (slot.available && onSlotSelect) {
      onSlotSelect(slot);
    }
  }, [onSlotSelect]);

  // Group slots by time periods for better UX
  const groupedSlots = useMemo(() => {
    const groups = {
      morning: { label: 'Morning', slots: [] },
      afternoon: { label: 'Afternoon', slots: [] },
      evening: { label: 'Evening', slots: [] }
    };

    availableSlots.forEach(slot => {
      const hour = slot.start.getHours();
      if (hour < 12) {
        groups.morning.slots.push(slot);
      } else if (hour < 17) {
        groups.afternoon.slots.push(slot);
      } else {
        groups.evening.slots.push(slot);
      }
    });

    return groups;
  }, [availableSlots]);

  const availableCount = useMemo(() => {
    return availableSlots.filter(slot => slot.available).length;
  }, [availableSlots]);

  const isSlotSelected = useCallback((slot) => {
    return selectedSlot && selectedSlot.start.getTime() === slot.start.getTime();
  }, [selectedSlot]);

  if (loading || timeSlotsLoading) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <Icon name="Loader" size={24} className="animate-spin mx-auto mb-2" />
        <p className="text-text-secondary">
          {timeSlotsLoading ? 'Loading time slot configuration...' : 'Loading available time slots...'}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        <div className="flex items-center gap-2 text-red-700">
          <Icon name="AlertCircle" size={16} />
          <span className="text-sm">{error}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadAvailableSlots}
          className="mt-2"
        >
          <Icon name="RefreshCw" size={14} className="mr-1" />
          Retry
        </Button>
      </div>
    );
  }

  const dateDisplay = selectedDate.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Check if there are configured time slots for this day
  const dayOfWeek = selectedDate.getDay();
  const hasConfiguredSlots = timeSlots.some(slot => slot.day_of_week === dayOfWeek);
  
  if (!hasConfiguredSlots) {
    const handleConfigureClick = () => {
      navigate('/interview-management?modal=timeSlotManager');
    };

    return (
      <div className={`text-center py-8 border border-border rounded-lg bg-gray-50 ${className}`}>
        <Icon name="Calendar" size={48} className="mx-auto mb-4 text-text-secondary" />
        <h4 className="font-medium mb-2">No time slots configured</h4>
        <p className="text-text-secondary text-sm mb-4">
          No available time slots are configured for {selectedDate.toLocaleDateString('en-US', { weekday: 'long' })}s.
          Please configure your available hours first.
        </p>
        <Button
          variant="primary"
          size="sm"
          onClick={handleConfigureClick}
          className="inline-flex items-center gap-2"
        >
          <Icon name="Plus" size={16} />
          Configure Time Slots
        </Button>
      </div>
    );
  }

  if (availableSlots.length === 0) {
    return (
      <div className={`text-center py-8 border border-border rounded-lg bg-gray-50 ${className}`}>
        <Icon name="Clock" size={48} className="mx-auto mb-4 text-text-secondary" />
        <h4 className="font-medium mb-2">No available time slots</h4>
        <p className="text-text-secondary text-sm">
          All time slots are booked for {dateDisplay}. Try selecting a different date.
        </p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <h4 className="font-medium">
          Available Times for {dateDisplay}
        </h4>
        <div className="text-sm text-text-secondary">
          {availableCount} available slot{availableCount !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-text-secondary">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-primary rounded"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-gray-300 rounded"></div>
          <span>Busy</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-primary-600 rounded"></div>
          <span>Selected</span>
        </div>
      </div>

      {/* Time slots grouped by period */}
      <div className="space-y-4">
        {Object.entries(groupedSlots).map(([period, group]) => (
          group.slots.length > 0 && (
            <div key={period}>
              <h5 className="text-sm font-medium text-text-secondary mb-2">
                {group.label}
              </h5>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {group.slots.map((slot, index) => (
                  <Button
                    key={`${period}-${index}`}
                    variant={isSlotSelected(slot) ? "primary" : slot.available ? "outline" : "ghost"}
                    size="sm"
                    onClick={() => handleSlotClick(slot)}
                    disabled={!slot.available}
                    className={`text-xs py-2 relative ${
                      !slot.available 
                        ? 'opacity-50 cursor-not-allowed bg-gray-100' 
                        : isSlotSelected(slot)
                        ? 'bg-primary-600 text-white'
                        : 'hover:border-primary hover:text-primary'
                    }`}
                    title={
                      slot.available 
                        ? `Available • ${formatTimeRange(slot.start, slot.end)}`
                        : `Busy • ${slot.concurrentCount}/${slot.maxConcurrent} booked`
                    }
                  >
                    <div className="flex flex-col items-center">
                      <span>{formatTime(slot.start)}</span>
                      {!slot.available && (
                        <span className="text-xs opacity-75">
                          {slot.concurrentCount}/{slot.maxConcurrent}
                        </span>
                      )}
                    </div>
                    {isSlotSelected(slot) && (
                      <Icon name="Check" size={12} className="absolute top-1 right-1" />
                    )}
                  </Button>
                ))}
              </div>
            </div>
          )
        ))}
      </div>

      {/* Selected slot confirmation */}
      {selectedSlot && (
        <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
          <div className="flex items-center gap-2 text-primary">
            <Icon name="Check" size={16} />
            <span className="text-sm font-medium">
              Selected: {formatTimeRange(selectedSlot.start, selectedSlot.end)}
            </span>
          </div>
          <div className="text-xs text-primary-700 mt-1">
            Duration: {duration} minutes
            {selectedSlot.bufferTime > 0 && ` • ${selectedSlot.bufferTime} min buffer after`}
          </div>
        </div>
      )}

      {/* Refresh button */}
      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => loadAvailableSlots(selectedDate, duration, excludeInterviewId, employerId)}
          className="text-text-secondary hover:text-text-primary"
        >
          <Icon name="RefreshCw" size={14} className="mr-1" />
          Refresh availability
        </Button>
      </div>
    </div>
  );
};

export default TimeSlotSelector;